/**
 * The e2e publishing seam. The client publishes viewer state, stage marks and the
 * request ledger here; the desktop hook (`desktop/viewer/src/e2e.ts`) reads them and
 * puts them on `window.__primepointE2E`. The client never imports desktop code.
 *
 * Cost when e2e is off: every `e2ePublish` call is a call plus one branch on the
 * module-level `enabled` boolean. No allocation per call, no `performance` call, no DOM
 * read; the module's own state (four maps, two arrays) is allocated once at load.
 * Hot-path callers (tile paint, axios, fetch) test `isE2E()` first so even the call
 * is skipped.
 *
 * `enabled` is read once, at module load: the desktop shell appends `?e2e=1` to the
 * window URL under `PRIMEPOINT_E2E=1`, and the hook is installed before the app tree
 * mounts. A host that installs the hook before this module loads (a browser harness)
 * is caught by the second test.
 */
import type { E2EViewerReadout } from "../embedpdf/pdfUiTypes";

export type E2EBytesSource = "opfs" | "store" | "network" | "local" | "unknown";
export type E2ENetworkTransport = "axios" | "native";

/** What a viewer component registers when it starts an open. `runtime` is the identity
 *  the PDF runtime reports tile events under (the runtime object itself). */
export interface E2EViewerRegistration {
    runtime: object;
    documentKey: string;
    fileName: string | null;
    sheetCode: string | null;
    readout(): E2EViewerReadout | null;
}

export interface E2EViewerState extends E2EViewerReadout {
    /** Monotonic registration identity; distinguishes a remounted viewer of the same document. */
    instanceId: number;
    documentKey: string;
    fileName: string | null;
    sheetCode: string | null;
    firstTilePaintedAt: number | null;
    lastReplacementAt: number | null;
    bytesSource: E2EBytesSource | null;
    bytesMs: number | null;
    openMs: number | null;
}

export interface E2ENetworkEntry {
    method: string;
    /** Path only: never a query string, a host, a header or a body. */
    path: string;
    status: number | null;
    startTime: number;
    durationMs: number | null;
    transport: E2ENetworkTransport;
}

interface E2EReactCommitScopeReport {
    count: number;
    totalMs: number;
    maxMs: number;
}

export interface E2EReactCommitReport extends E2EReactCommitScopeReport {
    /** E2E-only nested profiler attribution. The aggregate remains the root route
     * profiler so existing budget metrics do not double count nested scopes. */
    scopes: Record<string, E2EReactCommitScopeReport>;
}

/** One first-party analytics event as the transport would have posted it: the tenant
 *  stamp and the contract properties, never the member identity. Under e2e the
 *  transport publishes here instead of POSTing, so a tour run writes no usage events
 *  with the owner's identity into production analytics and the ledger stays GET-only. */
export interface E2EAnalyticsEvent {
    name: string;
    orgSlug: string;
    eventId: string;
    occurredAt: string;
    properties: Record<string, string | number | boolean>;
}

/** A sheet the viewer can name by file id: what a connection hit area publishes as its
 *  target document and sheet code. */
export interface E2ESheetIdentity {
    documentId: string;
    sheetCode: string;
}

interface ViewerSlot {
    instanceId: number;
    registration: E2EViewerRegistration;
    firstTilePaintedAt: number | null;
    lastReplacementAt: number | null;
    bytesSource: E2EBytesSource | null;
    bytesMs: number | null;
    openMs: number | null;
}

const MARK_PREFIX = "primepoint:";
/** A long session keeps this many ledger rows. Past the cap the oldest read row goes;
 *  a write row (anything but GET, HEAD, OPTIONS) is never dropped, so the runner's
 *  read-only gate at quit still sees a write a chatty step made early. */
const MAX_NETWORK_ENTRIES = 5_000;
const READ_METHODS: Record<string, true> = { GET: true, HEAD: true, OPTIONS: true };
const MAX_ANALYTICS_ENTRIES = 1_000;
const MAX_BYTES_SOURCES = 64;

function detectE2E(): boolean {
    if (typeof window === "undefined") return false;
    try {
        if (new URLSearchParams(window.location.search).get("e2e") === "1") return true;
    } catch {
        // A host with no usable location (a worker, a harness) is never e2e.
    }
    return (window as { __primepointE2E?: unknown }).__primepointE2E !== undefined;
}

const enabled = detectE2E();

export function isE2E(): boolean {
    return enabled;
}

const viewers = new Map<object, ViewerSlot>();
let activeViewer: ViewerSlot | null = null;
let nextViewerInstanceId = 0;
const network: E2ENetworkEntry[] = [];
const analytics: E2EAnalyticsEvent[] = [];
const bytesSources = new Map<string, E2EBytesSource>();
const sheetsByFileId = new Map<string, E2ESheetIdentity>();
let reactCommitCount = 0;
let reactCommitTotalMs = 0;
let reactCommitMaxMs = 0;
const reactCommitsByScope = new Map<string, E2EReactCommitScopeReport>();

function recordReactCommit(report: E2EReactCommitScopeReport, actualDurationMs: number): void {
    report.count += 1;
    report.totalMs += actualDurationMs;
    if (actualDurationMs > report.maxMs) report.maxMs = actualDurationMs;
}

export const e2ePublish = {
    /** Records `primepoint:<name>` at `performance.now()`. */
    mark(name: string, detail?: unknown): void {
        if (!enabled) return;
        try {
            performance.mark(MARK_PREFIX + name, detail === undefined ? undefined : { detail });
        } catch {
            // Diagnostics never block the app path.
        }
    },

    /** Records `primepoint:<name>` as a measure from `startTime` to now. */
    measure(name: string, startTime: number, detail?: unknown): void {
        if (!enabled) return;
        try {
            performance.measure(MARK_PREFIX + name, {
                start: startTime,
                end: performance.now(),
                ...(detail === undefined ? {} : { detail }),
            });
        } catch {
            // Diagnostics never block the app path.
        }
    },

    /** A viewer starts an open: it becomes the active viewer and its per-document
     *  stage fields reset. */
    viewerOpenStart(registration: E2EViewerRegistration): void {
        if (!enabled) return;
        const slot: ViewerSlot = {
            instanceId: ++nextViewerInstanceId,
            registration,
            firstTilePaintedAt: null,
            lastReplacementAt: null,
            bytesSource: null,
            bytesMs: null,
            openMs: null,
        };
        viewers.set(registration.runtime, slot);
        activeViewer = slot;
    },

    /** One open stage finished for `runtime`'s document: records the measure
     *  `primepoint:viewer:<stage>` from `startedAt` to now with the document key, and
     *  keeps `bytesMs`/`openMs` (and the bytes source the loader named) on the viewer. */
    viewerStage(runtime: object, stage: "bytes" | "open" | "fit-wait", startedAt: number): void {
        if (!enabled) return;
        const slot = viewers.get(runtime);
        if (!slot) return;
        const documentKey = slot.registration.documentKey;
        const ms = performance.now() - startedAt;
        if (stage === "bytes") {
            slot.bytesSource = bytesSources.get(documentKey) ?? "unknown";
            slot.bytesMs = ms;
        } else if (stage === "open") {
            slot.openMs = ms;
        }
        e2ePublish.measure(`viewer:${stage}`, startedAt, { documentKey });
    },

    /** A mark that names the active document of `runtime` without the caller knowing it. */
    viewerMark(runtime: object, name: string, detail?: Record<string, unknown>): void {
        if (!enabled) return;
        const slot = viewers.get(runtime);
        if (!slot) return;
        e2ePublish.mark(`viewer:${name}`, { documentKey: slot.registration.documentKey, ...detail });
    },

    viewerFirstTile(runtime: object): void {
        if (!enabled) return;
        const slot = viewers.get(runtime);
        if (slot && slot.firstTilePaintedAt === null) slot.firstTilePaintedAt = performance.now();
    },

    viewerCoverageComplete(runtime: object): void {
        if (!enabled) return;
        const slot = viewers.get(runtime);
        if (slot) slot.lastReplacementAt = performance.now();
    },

    viewerClosed(runtime: object): void {
        if (!enabled) return;
        const slot = viewers.get(runtime);
        if (!slot) return;
        viewers.delete(runtime);
        if (activeViewer === slot) activeViewer = null;
    },

    /** The loader names where a file's bytes came from; the viewer reads it back by
     *  file id once its own timing is known. */
    bytesSource(fileId: string, source: E2EBytesSource): void {
        if (!enabled) return;
        bytesSources.delete(fileId);
        bytesSources.set(fileId, source);
        if (bytesSources.size > MAX_BYTES_SOURCES) {
            const oldest = bytesSources.keys().next().value;
            if (oldest !== undefined) bytesSources.delete(oldest);
        }
    },

    /** Opens a ledger row. The caller fills `status`/`durationMs` on settle. */
    networkStart(method: string, path: string, transport: E2ENetworkTransport): E2ENetworkEntry {
        const entry: E2ENetworkEntry = {
            method: method.toUpperCase(),
            path,
            status: null,
            startTime: performance.now(),
            durationMs: null,
            transport,
        };
        network.push(entry);
        if (network.length > MAX_NETWORK_ENTRIES) {
            const oldestRead = network.findIndex((row) => READ_METHODS[row.method] === true);
            if (oldestRead !== -1) network.splice(oldestRead, 1);
        }
        return entry;
    },

    networkEnd(entry: E2ENetworkEntry, status: number | null): void {
        entry.status = status;
        entry.durationMs = performance.now() - entry.startTime;
    },

    reactCommit(scope: string, actualDurationMs: number, includeInAggregate = false): void {
        const scoped = reactCommitsByScope.get(scope) ?? { count: 0, totalMs: 0, maxMs: 0 };
        recordReactCommit(scoped, actualDurationMs);
        reactCommitsByScope.set(scope, scoped);
        if (includeInAggregate) {
            reactCommitCount += 1;
            reactCommitTotalMs += actualDurationMs;
            if (actualDurationMs > reactCommitMaxMs) reactCommitMaxMs = actualDurationMs;
        }
    },

    /** The sheets a viewer knows by file id, so a connection hit area can name its
     *  target document and sheet code without a lookup at click time. The caller gates
     *  on `isE2E()`; ungated here so a test can seed it. */
    sheets(entries: Iterable<{ fileId: string; documentId: string; sheetCode: string }>): void {
        for (const entry of entries) {
            sheetsByFileId.set(entry.fileId, { documentId: entry.documentId, sheetCode: entry.sheetCode });
        }
    },

    /** A first-party analytics event the transport would have posted. The caller
     *  (`analyticsEvent`) chooses this sink over the network only under `isE2E()`. */
    analytics(entry: E2EAnalyticsEvent): void {
        analytics.push(entry);
        if (analytics.length > MAX_ANALYTICS_ENTRIES) {
            analytics.splice(0, analytics.length - MAX_ANALYTICS_ENTRIES);
        }
    },
};

export const e2eRead = {
    viewer(): E2EViewerState | null {
        const slot = activeViewer;
        if (!slot) return null;
        const readout = slot.registration.readout();
        if (!readout) return null;
        return {
            instanceId: slot.instanceId,
            documentKey: slot.registration.documentKey,
            fileName: slot.registration.fileName,
            sheetCode: slot.registration.sheetCode,
            ...readout,
            firstTilePaintedAt: slot.firstTilePaintedAt,
            lastReplacementAt: slot.lastReplacementAt,
            bytesSource: slot.bytesSource,
            bytesMs: slot.bytesMs,
            openMs: slot.openMs,
        };
    },

    network(): E2ENetworkEntry[] {
        return network.map((entry) => ({ ...entry }));
    },

    clearNetwork(): void {
        network.length = 0;
    },

    analytics(): E2EAnalyticsEvent[] {
        return analytics.map((entry) => ({ ...entry, properties: { ...entry.properties } }));
    },

    clearAnalytics(): void {
        analytics.length = 0;
    },

    reactCommits(): E2EReactCommitReport {
        return {
            count: reactCommitCount,
            totalMs: reactCommitTotalMs,
            maxMs: reactCommitMaxMs,
            scopes: Object.fromEntries(
                Array.from(reactCommitsByScope, ([scope, report]) => [scope, { ...report }]),
            ),
        };
    },

    resetReactCommits(): void {
        reactCommitCount = 0;
        reactCommitTotalMs = 0;
        reactCommitMaxMs = 0;
        reactCommitsByScope.clear();
    },

    sheetForFile(fileId: string): E2ESheetIdentity | undefined {
        return sheetsByFileId.get(fileId);
    },
};

/** The path of a request URL, never its query string or host. A relative URL resolves
 *  against `base` (the document origin by default); an unparsable one is kept up to its
 *  first `?`. */
export function ledgerPath(input: string, base?: string): string {
    try {
        const origin = base ?? (typeof window === "undefined" ? "http://localhost/" : window.location.origin);
        return new URL(input, origin).pathname;
    } catch {
        const cut = input.indexOf("?");
        return cut === -1 ? input : input.slice(0, cut);
    }
}

/** True for an `http:`/`https:` URL (absolute or relative to the document origin). The
 *  Tauri IPC transport (`ipc:`, `tauri:`, `asset:`) also goes through `window.fetch`
 *  and is not an HTTP call. */
function isHttpUrl(input: string): boolean {
    try {
        const origin = typeof window === "undefined" ? "http://localhost/" : window.location.origin;
        const protocol = new URL(input, origin).protocol;
        return protocol === "http:" || protocol === "https:";
    } catch {
        return false;
    }
}

/** Wraps a `fetch` so every HTTP call lands in the ledger as a `native` row. Installed
 *  by the desktop shell around the transport it hands to `window.fetch`; a no-op wrapper
 *  when e2e is off. Custom-scheme fetches (the Tauri IPC bridge) are not HTTP and skip
 *  the ledger. */
export function ledgerFetch(fetchImpl: typeof fetch): typeof fetch {
    if (!enabled) return fetchImpl;
    return (input, init) => {
        const url =
            typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
        if (!isHttpUrl(url)) return fetchImpl(input, init);
        const method = init?.method ?? (typeof input === "object" && "method" in input ? input.method : "GET");
        const entry = e2ePublish.networkStart(method, ledgerPath(url), "native");
        return fetchImpl(input, init).then(
            (response) => {
                e2ePublish.networkEnd(entry, response.status);
                return response;
            },
            (error: unknown) => {
                e2ePublish.networkEnd(entry, null);
                throw error;
            },
        );
    };
}
